import React from 'react';
import {
  Box,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import UnfoldMoreIcon from '@mui/icons-material/UnfoldMore';
import { ReactComponent as VerticalDots } from 'assets/vertical-dots.svg';
import zeplinColor from '../../../theme/Color';
import Pagination from '../../shared/CustomPagination/CustomPagination';
import CustomSearch from '../../shared/CustomSearch/CustomSearch';
import CustomButton from '../../shared/CustomButton/CustomButton';
import { LightTooltip } from '../../shared/CustomTooltip/CustomTooltip';
import CustomPopOver from '../../shared/CustomPopOver/CustomPopOver';
import { SortPopover } from '../../shared/SortPopOver';
import { useAppSelector } from '../../../store/hooks';
import { TeamService } from '../../../service/team.service';
import { useParams } from 'react-router-dom';
import TeamMetricsGrid from './TeamMetricsGrid';
import AddAssignmentModal from './AddAssignmentModal';
import DeleteAssignmentModal from './DeleteAssignmentModal';
import DeleteIcon from 'assets/delete-red.png';
import EditIcon from 'assets/edit.svg';
import { usePermissions } from 'customHook/usePermissions';

type AssignmentRow = {
  id: string;
  clientId: string;
  name: string;
  category: string;
  capacityWeight: number;
  activeDeals: number;
  totalTasks: number;
};

type SortField = 'name' | 'category' | 'capacityWeight' | 'activeDeals' | 'totalTasks';
type SortOrder = 'asc' | 'desc';
type SortUiState = {
  anchorEl: Record<SortField, HTMLElement | null>;
  option: Record<SortField, SortOrder>;
};
const SEARCH_DEBOUNCE_MS = 2000;

const TeamAssignmentsTab = () => {
  const selectedMember = useAppSelector((state) => state.teamUi.selectedMember) || {};
  const params = useParams();
  const { hasAction } = usePermissions();
  const canCreateAssignment = hasAction('Teams', 'Assignments', 'create');

  const [addAssignmentOpen, setAddAssignmentOpen] = React.useState(false);
  const [rows, setRows] = React.useState<AssignmentRow[]>([]);
  const [workloadLoading, setWorkloadLoading] = React.useState(false);
  const [apiMetrics, setApiMetrics] = React.useState({
    workloadCapacity: 0,
    activeClients: 0,
    totalDeals: 0,
    totalTasks: 0,
  });
  const [nextToken, setNextToken] = React.useState<string | null>(null);
  const [searchInput, setSearchInput] = React.useState('');
  const [debouncedSearch, setDebouncedSearch] = React.useState('');
  const [menuAnchorEl, setMenuAnchorEl] = React.useState<HTMLElement | null>(null);
  const [selectedAssignmentId, setSelectedAssignmentId] = React.useState<string | null>(null);
  const [editingAssignment, setEditingAssignment] = React.useState<AssignmentRow | null>(null);
  const [deletingAssignment, setDeletingAssignment] = React.useState<AssignmentRow | null>(null);
  const [sortUi, setSortUi] = React.useState<SortUiState>({
    anchorEl: {
      name: null,
      category: null,
      capacityWeight: null,
      activeDeals: null,
      totalTasks: null,
    },
    option: {
      name: 'asc',
      category: 'asc',
      capacityWeight: 'asc',
      activeDeals: 'asc',
      totalTasks: 'asc',
    },
  });

  React.useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchInput.trim());
    }, SEARCH_DEBOUNCE_MS);

    return () => clearTimeout(timer);
  }, [searchInput]);

  const fetchWorkload = React.useCallback(
    (memberId: string, token?: string | null, limit?: number, query?: string, sort?: string) => {
      setWorkloadLoading(true);
      const params: string[] = [];
      if (limit) params.push(`limit=${limit}`);
      if (token) params.push(`nextToken=${encodeURIComponent(token)}`);
      if (query) params.push(`search=${encodeURIComponent(query)}`);
      if (sort) params.push(`sort=${encodeURIComponent(sort)}`);
      const url = params.length ? `${memberId}?${params.join('&')}` : memberId;
      TeamService.getMemberWorkload(url)
        .then((data: any) => {
          const mapped: AssignmentRow[] = (data?.assignments || []).map((item: any) => ({
            id: item.assignmentID,
            clientId: item.clientId || item.clientID || item.accountId || item.AccountId || '',
            name: item.clientName,
            category: item.category,
            capacityWeight: item.capacityWeight,
            activeDeals: item.activeDeals,
            totalTasks: item.totalTasks,
          }));
          setRows((prev) => (token ? [...prev, ...mapped] : mapped));
          setNextToken(data?.nextToken ?? null);
          setApiMetrics({
            workloadCapacity: data?.totalWorkloadCapacity ?? 0,
            activeClients: data?.activeClients ?? 0,
            totalDeals: data?.totalDeals ?? 0,
            totalTasks: data?.totalTasks ?? 0,
          });
        })
        .catch(() => {
          setRows([]);
          setNextToken(null);
        })
        .finally(() => setWorkloadLoading(false));
    },
    [],
  );

  const [sortField, setSortField] = React.useState<SortField>('name');
  const [sortOrder, setSortOrder] = React.useState<SortOrder>('asc');
  const sortQuery = React.useMemo(() => {
    const fieldMap: Record<SortField, string> = {
      name: 'name',
      category: 'category',
      capacityWeight: 'workLoadCapacity',
      activeDeals: 'activeDeals',
      totalTasks: 'assignmentCount',
    };

    const primarySort = `${fieldMap[sortField]}:${sortOrder}`;
    return sortField === 'name' ? primarySort : `${primarySort},name:asc`;
  }, [sortField, sortOrder]);
  const [currentPage, setCurrentPage] = React.useState(0);
  const [rowsPerPage, setRowsPerPage] = React.useState(10);
  const [pageWindowStart, setPageWindowStart] = React.useState(0);

  React.useEffect(() => {
    const memberId = params.id || selectedMember?.id;
    if (!memberId) return;
    setRows([]);
    setNextToken(null);
    setCurrentPage(0);
    setPageWindowStart(0);
    fetchWorkload(memberId, null, rowsPerPage, debouncedSearch, sortQuery);
  }, [params.id, selectedMember?.id, rowsPerPage, debouncedSearch, sortQuery, fetchWorkload]);

  const paginatedRows = React.useMemo(() => {
    const start = currentPage * rowsPerPage;
    return rows.slice(start, start + rowsPerPage);
  }, [rows, currentPage, rowsPerPage]);

  const totalPages = Math.max(1, Math.ceil(rows.length / rowsPerPage));

  const handleSortClick = (field: SortField) => (event: React.MouseEvent<HTMLElement>) => {
    setSortUi((prev) => ({
      ...prev,
      anchorEl: {
        ...prev.anchorEl,
        [field]: event.currentTarget,
      },
    }));
  };

  const applySort = (field: SortField, order: 'asc' | 'desc') => {
    setSortField(field);
    setSortOrder(order);
    setSortUi((prev) => ({
      ...prev,
      option: {
        ...prev.option,
        [field]: order,
      },
      anchorEl: {
        ...prev.anchorEl,
        [field]: null,
      },
    }));
  };

  const handleNameSortChange = (value: 'asc' | 'desc') => {
    applySort('name', value);
  };

  const handleCategorySortChange = (value: 'asc' | 'desc') => {
    applySort('category', value);
  };

  const handleCapacitySortChange = (value: 'asc' | 'desc') => {
    applySort('capacityWeight', value);
  };

  const handleActiveDealsSortChange = (value: 'asc' | 'desc') => {
    applySort('activeDeals', value);
  };

  const handleTotalTasksSortChange = (value: 'asc' | 'desc') => {
    applySort('totalTasks', value);
  };

  const getSortAnchorSetter =
    (field: SortField) => (value: React.SetStateAction<HTMLElement | null>) => {
      setSortUi((prev) => ({
        ...prev,
        anchorEl: {
          ...prev.anchorEl,
          [field]: typeof value === 'function' ? value(prev.anchorEl[field]) : value,
        },
      }));
    };

  const teamMemberName = React.useMemo(() => {
    const firstName = selectedMember?.firstName || '';
    const lastName = selectedMember?.lastName || '';
    const fullName = `${firstName} ${lastName}`.trim();
    return fullName || selectedMember?.name || 'this team member';
  }, [selectedMember]);

  const metrics = apiMetrics;
  const assignmentMenuOpen = Boolean(menuAnchorEl);
  const assignmentMenuId = assignmentMenuOpen ? 'simple-popover' : undefined;

  const handleOpenAssignmentMenu = (event: React.MouseEvent<HTMLElement>, id: string) => {
    event.stopPropagation();
    setSelectedAssignmentId(id);
    setMenuAnchorEl(event.currentTarget);
  };

  const handleCloseAssignmentMenu = () => {
    setMenuAnchorEl(null);
    setSelectedAssignmentId(null);
  };

  const refreshAssignments = React.useCallback(() => {
    const memberId = params.id || selectedMember?.id;
    if (!memberId) return;
    setRows([]);
    setNextToken(null);
    setCurrentPage(0);
    setPageWindowStart(0);
    fetchWorkload(memberId, null, rowsPerPage, debouncedSearch, sortQuery);
  }, [params.id, selectedMember?.id, rowsPerPage, debouncedSearch, sortQuery, fetchWorkload]);

  const handleEditAssignment = () => {
    const assignment = rows.find((row) => row.id === selectedAssignmentId) || null;
    if (!assignment) {
      handleCloseAssignmentMenu();
      return;
    }

    setEditingAssignment(assignment);
    setAddAssignmentOpen(true);
    handleCloseAssignmentMenu();
  };

  const handleDeleteAssignment = () => {
    const assignment = rows.find((row) => row.id === selectedAssignmentId) || null;
    if (!assignment) {
      handleCloseAssignmentMenu();
      return;
    }

    setDeletingAssignment(assignment);
    handleCloseAssignmentMenu();
  };

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        gap: '16px',
      }}
    >
      <Box
        sx={{
          position: 'absolute',
          top: 0,
          right: 0,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          gap: '12px',
        }}
      >
        <Box sx={{ width: { xs: '100%', xl: '30rem' } }}>
          <CustomSearch
            getProjects={(value: string) => {
              setSearchInput(value);
              setCurrentPage(0);
              setPageWindowStart(0);
            }}
          />
        </Box>

        <LightTooltip
          title={
            <Typography variant='responsiveBody3' color={zeplinColor.Neutral1}>
              New Assignment
            </Typography>
          }
          placement='bottom-end'
          style={{ backgroundColor: zeplinColor.BasicBlack, borderRadius: '8px' }}
        >
          <CustomButton
            disabled={!canCreateAssignment}
            onClick={() => {
              setEditingAssignment(null);
              setAddAssignmentOpen(true);
            }}
            variant='contained'
            color={'primary'}
            buttonVariant='responsiveButtonLarge'
            style={{
              width: 'fit-content',
              borderRadius: { xs: '10px', xl: '14px' },
              height: { xs: 30, xl: 48 },
              padding: { xs: '10px 12px', xl: '14px 16px' },
              '&.Mui-focusVisible': { backgroundColor: '#333CAA' },
            }}
            textStyle={{ color: zeplinColor.Neutral1, width: 'fit-content' }}
            text={'New Assignment'}
            iconPosition='start'
            icon={
              <img
                src='/assets/add-white.svg'
                alt='add-assignment'
                style={{ width: 14, height: 14 }}
              />
            }
          />
        </LightTooltip>
      </Box>

      <TeamMetricsGrid metrics={metrics} />

      <TableContainer sx={{ borderRadius: '14px', border: `1px solid ${zeplinColor.Neutral5}` }}>
        <Table stickyHeader sx={{ minWidth: 900 }}>
          <TableHead>
            <TableRow
              sx={{
                borderBottom: `1px solid ${zeplinColor.Neutral5}`,
                '& th': {
                  backgroundColor: zeplinColor.Neutral3,
                  color: zeplinColor.BasicBlack,
                },
                '& th:first-of-type': {
                  borderRadius: '14px 0px 0px 14px',
                },
                '& th:last-child': {
                  borderRadius: '0px 14px 14px 0px',
                },
              }}
            >
              <TableCell sx={{ p: '12px 16px' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <Typography variant='responsiveBody3'>Name</Typography>
                  <IconButton size='small' onClick={handleSortClick('name')} sx={{ p: '2px' }}>
                    <UnfoldMoreIcon sx={{ fontSize: 16, color: zeplinColor.BasicBlack }} />
                  </IconButton>
                </Box>
              </TableCell>
              <TableCell sx={{ p: '12px 16px' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <Typography variant='responsiveBody3'>Category</Typography>
                  <IconButton size='small' onClick={handleSortClick('category')} sx={{ p: '2px' }}>
                    <UnfoldMoreIcon sx={{ fontSize: 16, color: zeplinColor.BasicBlack }} />
                  </IconButton>
                </Box>
              </TableCell>
              <TableCell sx={{ p: '12px 16px' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <Typography variant='responsiveBody3'>Capacity Weight</Typography>
                  <IconButton
                    size='small'
                    onClick={handleSortClick('capacityWeight')}
                    sx={{ p: '2px' }}
                  >
                    <UnfoldMoreIcon sx={{ fontSize: 16, color: zeplinColor.BasicBlack }} />
                  </IconButton>
                </Box>
              </TableCell>
              <TableCell sx={{ p: '12px 16px' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <Typography variant='responsiveBody3'>Active Deals</Typography>
                  <IconButton
                    size='small'
                    onClick={handleSortClick('activeDeals')}
                    sx={{ p: '2px' }}
                  >
                    <UnfoldMoreIcon sx={{ fontSize: 16, color: zeplinColor.BasicBlack }} />
                  </IconButton>
                </Box>
              </TableCell>
              <TableCell sx={{ p: '12px 16px' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <Typography variant='responsiveBody3'>Total Tasks</Typography>
                  <IconButton
                    size='small'
                    onClick={handleSortClick('totalTasks')}
                    sx={{ p: '2px' }}
                  >
                    <UnfoldMoreIcon sx={{ fontSize: 16, color: zeplinColor.BasicBlack }} />
                  </IconButton>
                </Box>
              </TableCell>
              <TableCell align='center' sx={{ p: '12px 16px', width: 48 }} />
            </TableRow>
          </TableHead>

          <TableBody>
            {paginatedRows.length === 0 && !workloadLoading && (
              <TableRow>
                <TableCell colSpan={6} sx={{ border: 'none', p: '48px 16px' }}>
                  <Box
                    sx={{
                      display: 'flex',
                      flexDirection: 'column',
                      gap: '12px',
                      justifyContent: 'center',
                      alignItems: 'center',
                      textAlign: 'center',
                    }}
                  >
                    <img
                      src={'/assets/no-chart.svg'}
                      width={'36px'}
                      height={'36px'}
                      alt='no-data'
                    />
                    <Typography variant='responsiveHeading3'>No Data Available</Typography>
                    <Typography variant='responsiveBody3' color={zeplinColor.Neutral8}>
                      There's no data to display right now. Data will appear here automatically once
                      available.
                    </Typography>
                  </Box>
                </TableCell>
              </TableRow>
            )}
            {paginatedRows.map((item) => (
              <TableRow
                key={item.id}
                sx={{
                  borderBottom: `1px solid ${zeplinColor.Neutral5}`,
                  '&:hover': { backgroundColor: zeplinColor.Neutral2 },
                }}
              >
                <TableCell sx={{ p: '12px 16px' }}>
                  <Typography variant='body3' color={zeplinColor.BasicBlack}>
                    {item.name}
                  </Typography>
                </TableCell>
                <TableCell sx={{ p: '12px 16px' }}>
                  <Typography variant='body3' color={zeplinColor.BasicBlack}>
                    {item.category}
                  </Typography>
                </TableCell>
                <TableCell sx={{ p: '12px 16px' }}>
                  <Typography variant='body3' color={zeplinColor.BasicBlack}>
                    {item.capacityWeight}%
                  </Typography>
                </TableCell>
                <TableCell sx={{ p: '12px 16px' }}>
                  <Typography variant='body3' color={zeplinColor.Brand1}>
                    {item.activeDeals}
                  </Typography>
                </TableCell>
                <TableCell sx={{ p: '12px 16px' }}>
                  <Typography variant='body3' color={zeplinColor.Brand1}>
                    {item.totalTasks}
                  </Typography>
                </TableCell>
                <TableCell align='center' sx={{ p: '12px 16px' }}>
                  <Box
                    sx={{
                      width: 24,
                      height: 24,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      margin: '0 auto',
                    }}
                  >
                    <VerticalDots
                      onClick={(event: any) => handleOpenAssignmentMenu(event, item.id)}
                      style={{ width: 20, height: 20, cursor: 'pointer' }}
                    />
                  </Box>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <CustomPopOver
        onClick={(event: any) => event.stopPropagation()}
        setAnchorEl={setMenuAnchorEl}
        anchorEl={menuAnchorEl}
        id={assignmentMenuId}
        sx={{ borderRadius: '14px', boxShadow: 'rgba(0, 0, 0, 0.15)' }}
      >
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: '16px',
            padding: '16px',
            minWidth: '140px',
          }}
        >
          <Box
            sx={{ display: 'flex', gap: '9px', cursor: 'pointer' }}
            onClick={handleEditAssignment}
          >
            <img src={EditIcon} width={18} height={18} />
            <Typography variant='responsiveBody3' color={zeplinColor.BasicBlack}>
              Edit
            </Typography>
          </Box>
          <Box
            sx={{ display: 'flex', gap: '9px', cursor: 'pointer' }}
            onClick={handleDeleteAssignment}
          >
            <img src={DeleteIcon} width={18} height={18} />
            <Typography variant='responsiveBody3' color={zeplinColor.Error}>
              Unassign
            </Typography>
          </Box>
        </Box>
      </CustomPopOver>

      <Pagination
        setCurrentPage={setCurrentPage}
        setRowsPerPage={setRowsPerPage}
        currentPage={currentPage}
        totalPages={totalPages}
        rowsPerPage={rowsPerPage}
        count={rows.length}
        paginatedDataLength={paginatedRows.length}
        onFirstPage={() => {
          setCurrentPage(0);
          setPageWindowStart(0);
        }}
        onPreviousPage={() => setCurrentPage((prev) => Math.max(prev - 1, 0))}
        onNextPage={() => {
          const nextPageIndex = currentPage + 1;
          const isLastLoadedPage = (nextPageIndex + 1) * rowsPerPage >= rows.length;
          if (isLastLoadedPage && nextToken) {
            const memberId = params.id || selectedMember?.id;
            if (memberId)
              fetchWorkload(memberId, nextToken, rowsPerPage, debouncedSearch, sortQuery);
          }
          setCurrentPage((prev) => Math.min(prev + 1, totalPages - 1));
        }}
        onLastPage={() => {
          const last = Math.max(totalPages - 1, 0);
          setCurrentPage(last);
          setPageWindowStart(Math.max(0, totalPages - 4));
        }}
        onPageChange={(page) => setCurrentPage(page)}
        onRowsPerPageChange={(value) => {
          setRowsPerPage(value);
          setCurrentPage(0);
          setPageWindowStart(0);
        }}
        pageWindowStart={pageWindowStart}
        setPageWindowStart={setPageWindowStart}
      />

      <AddAssignmentModal
        open={addAssignmentOpen}
        onClose={() => {
          setAddAssignmentOpen(false);
          setEditingAssignment(null);
        }}
        currentCapacity={apiMetrics.workloadCapacity}
        memberId={String(params.id || selectedMember?.id || '')}
        assignment={
          editingAssignment
            ? {
                id: editingAssignment.id,
                clientId: editingAssignment.clientId,
                clientName: editingAssignment.name,
                capacityWeight: editingAssignment.capacityWeight,
              }
            : null
        }
        onSaved={refreshAssignments}
      />

      <DeleteAssignmentModal
        open={Boolean(deletingAssignment)}
        assignmentId={deletingAssignment?.id}
        assignmentName={deletingAssignment?.name}
        teamMemberName={teamMemberName}
        capacityWeight={deletingAssignment?.capacityWeight}
        onClose={() => setDeletingAssignment(null)}
        onDeleted={refreshAssignments}
      />

      <SortPopover
        anchorEl={sortUi.anchorEl.name}
        setAnchorEl={getSortAnchorSetter('name')}
        sortOption={sortUi.option.name}
        onSortChange={handleNameSortChange}
        label='Name'
        ascLabel='A to Z'
        descLabel='Z to A'
      />

      <SortPopover
        anchorEl={sortUi.anchorEl.category}
        setAnchorEl={getSortAnchorSetter('category')}
        sortOption={sortUi.option.category}
        onSortChange={handleCategorySortChange}
        label='Category'
        ascLabel='A to Z'
        descLabel='Z to A'
      />

      <SortPopover
        anchorEl={sortUi.anchorEl.capacityWeight}
        setAnchorEl={getSortAnchorSetter('capacityWeight')}
        sortOption={sortUi.option.capacityWeight}
        onSortChange={handleCapacitySortChange}
        label='Capacity Weight'
        ascLabel='Lowest to Highest'
        descLabel='Highest to Lowest'
      />

      <SortPopover
        anchorEl={sortUi.anchorEl.activeDeals}
        setAnchorEl={getSortAnchorSetter('activeDeals')}
        sortOption={sortUi.option.activeDeals}
        onSortChange={handleActiveDealsSortChange}
        label='Active Deals'
        ascLabel='Lowest to Highest'
        descLabel='Highest to Lowest'
      />

      <SortPopover
        anchorEl={sortUi.anchorEl.totalTasks}
        setAnchorEl={getSortAnchorSetter('totalTasks')}
        sortOption={sortUi.option.totalTasks}
        onSortChange={handleTotalTasksSortChange}
        label='Total Tasks'
        ascLabel='Lowest to Highest'
        descLabel='Highest to Lowest'
      />
    </Box>
  );
};

export default TeamAssignmentsTab;
